import { Component, OnInit, Input, OnDestroy, Inject } from '@angular/core';
import { hd, TicketContextService, DeviceService, FeatureToggleService, GlobalEventBusService } from '@nx-fe/services';
import { XsI18nPipe } from '@nx-fe/pipes';
import { GlobalEvents } from '../../../../../core/global-event-constants';
import { Subscription } from 'rxjs';
import { RemoteControlService } from 'xsmodules/remote-control/services/remote-control.service';
import { RemoteControlDeviceState, DeviceStateRegistryService } from 'app/modules/remote-control/services/device-state-registry.service';
import { BroadcastChannels, BroadcastService } from 'app/utils/broadcastService';
import { ActivityContextService } from '../activity/activity-context';
import { WorkPanelManagerService, WorkPanelType } from 'xsmodules/remote-control/services/work-panel-manager.service';
import { RemoteControlActivity } from 'xsmodules/ticket/ticket.model';
import { TicketPageService } from 'xsmodules/ticket/ticket-page.service';
import { IWorkPanelWindow } from 'xsmodules/remote-control/services/work-panel.service';

type IDeviceModel = hd.IDeviceModel;

@Component({
  selector: 'xs-remote-control-activity',
  templateUrl: './remote-control-activity.component.html',
  styleUrls: ['./remote-control-activity.component.scss'],
})
export class RemoteControlActivityComponent implements OnInit, OnDestroy {
  @Input() activity: RemoteControlActivity;
  @Input() activityCtrl: ActivityContextService;

  canEdit: boolean;
  deviceId: string;
  deviceState: RemoteControlDeviceState;
  duration: string;
  isNewSession = true;
  error: string;
  message: string;
  isExternallyDisabled = false;

  private externalDisableSubcription: Subscription;
  private $window: IWorkPanelWindow = window as any;

  private readonly popoutRemoteControlStop: BroadcastService<IDeviceModel> = new BroadcastService<IDeviceModel>(
    BroadcastChannels.onPopoutRemoteControlStop
  );
  private readonly popoutRemoteControlStopped: BroadcastService<void> = new BroadcastService<void>(
    BroadcastChannels.onPopoutRemoteControlStopped
  );

  get showPopout(): boolean {
    const deviceId = this.deviceState?.device?.mcDeviceId;
    return !!deviceId && !this.remoteControlService.isVideoStreaming(deviceId);
  }

  constructor(
    private featureToggleService: FeatureToggleService,
    private ticketContextService: TicketContextService,
    public remoteControlService: RemoteControlService,
    public deviceStateRegistry: DeviceStateRegistryService,
    private $filter: XsI18nPipe,
    private deviceService: DeviceService,
    private workPanelManagerService: WorkPanelManagerService,
    private globalEventBusService: GlobalEventBusService,
    private ticketPageService: TicketPageService
  ) {
    this.externalDisableSubcription = this.remoteControlService.externalPopoutDisabled$.subscribe(isDisabled => {
      this.isExternallyDisabled = isDisabled;
    });
  }

  ngOnInit() {
    // Is this a new RC session (i.e. activity that is not a historic log)?
    this.isNewSession = this.activity.activityId == null;

    this.setDeviceId(this.activity.details?.deviceId);

    // If this is a new session, register for callbacks
    if (this.isNewSession) {
      if (this.isOpenSession()) {
        // Session is already open - likely due to transfer from stand-alone RC page
        this.deviceStateRegistry.setRemoteControlEndedCallback(this.sessionEnded, this.deviceId);
        this.durationUpdate();
      } else {
        // Brand new session, listen for connected callback
        this.deviceStateRegistry.setRemoteControlStartedCallback(this.sessionStarted, this.activity.details.deviceId);

        this.popoutRemoteControlStopped.onMessage = () => {
          this.sessionEnded();
        };
      }
      this.deviceStateRegistry.setRemoteControlErrorCallback(this.sessionError, this.activity.details.deviceId);
      this.deviceStateRegistry.setRemoteControlLoginCallback(this.sessionLogin, this.activity.details.deviceId);
    } else {
      this.durationUpdate();
    }

    if (this.deviceState && !this.deviceState.device) {
      this.deviceService.getDevice(this.activity.details?.deviceId).subscribe(
        (device: IDeviceModel) => {
          this.deviceState.device = device;
        },
        (error: any) => {
          console.log(error);
          throw new Error('Failed to load device details: ' + error);
        }
      );
    }

    this.updateActivityState();
  }

  ngOnDestroy(): void {
    if (this.externalDisableSubcription) {
      this.externalDisableSubcription.unsubscribe();
    }

    if (this.popoutRemoteControlStop) {
      this.popoutRemoteControlStop.close();
    }

    if (this.popoutRemoteControlStopped) {
      this.popoutRemoteControlStopped.close();
    }
  }

  isLoadingMessageVisible = (): boolean =>
    this.message && this.message !== '' && this.activityCtrl.getActivityState() === 'active';

  /** Formats RC session time for display */
  formatTime(time: number) {
    const timeHours = Math.floor(time / (60 * 60));
    const timeMinutes = Math.floor(time / 60) % 60;
    const timeSeconds = Math.floor(time % 60);
    let formatedTime = '';
    let formatedHours = '';
    let formatedMins = '';
    let formatedSecs = '';
    if (timeHours > 0) {
      formatedHours = this.$filter.transform('remoteControlController_hours_Format', timeHours);
    }
    if (timeMinutes > 0) {
      formatedMins = this.$filter.transform('remoteControlController_mins_Format', timeMinutes);
    }
    if (timeSeconds >= 0) {
      if (timeSeconds !== 0 || (timeMinutes === 0 && timeHours === 0)) {
        formatedSecs = this.$filter.transform('remoteControlController_secs_Format', timeSeconds);
      }
    }

    formatedTime = this.$filter.transform(
      'remoteControlController_timeOrder_Format',
      formatedHours,
      formatedMins,
      formatedSecs
    );
    formatedTime = formatedTime.trim();
    return formatedTime;
  }

  forceRemoteControlDevice() {
    // RcContext.instance.forceOverride = true;
    if (!this.activity.creationDate) {
      this.activity.creationDate = new Date().toISOString();
    }
    this.clearError();
    this.remoteControlService
      .startRemoteControl(this.deviceState.device, this.ticketContextService.ticket.ticketId)
      .then(() => {
        this.deviceStateRegistry.setRemoteControlEndedCallback(this.sessionEnded, this.deviceId);
        this.setDeviceState();
        this.deviceState.connected = true;
        this.durationUpdate();
      });
  }

  private timerCount = 0;
  /** Computes and updates the duration of the RC session */
  durationUpdate() {
    if (!this.activity || !this.deviceState.connected || !this.deviceStateRegistry.isCurrentRemoteControlRunning()) {
      return;
    }

    if (!this.activity.creationDate) {
      this.activity.creationDate = new Date().toISOString();
    }

    const endTime = !this.activity.details?.terminationDate
      ? new Date()
      : new Date(this.activity.details.terminationDate);

    const creation = new Date(this.activity.creationDate);
    const duration = +endTime - +creation;

    this.duration = this.formatTime(duration / 1000);

    if (this.isOpenSession()) {
      if (this.timerCount === 0) {
        this.timerCount++;
        setTimeout(() => {
          this.timerCount--;
          this.durationUpdate();
        }, 1000 - (duration % 1000));
      }
    }
  }

  getDeviceName(): string {
    if (this.deviceState?.device?.deviceName) {
      return this.deviceState.device.deviceName;
    }
    return this.$filter.transform('device_Info_Unavailable');
  }

  getDuration(): string {
    if (!this.duration) {
      this.durationUpdate();
    }
    return this.duration;
  }

  /** Called when the RC session has started */
  sessionStarted = () => {
    if (!this.activity.creationDate) {
      this.activity.creationDate = new Date().toISOString();
    }

    this.message = null;
    this.deviceStateRegistry.removeRemoteControlStartedCallback(this.sessionStarted, this.deviceId);
    this.deviceStateRegistry.setRemoteControlEndedCallback(this.sessionEnded, this.deviceId);

    this.setDeviceState();
    this.clearError();
    this.durationUpdate();
  };

  sessionLogin = () => {
    this.deviceStateRegistry.removeRemoteControlLoginCallback(this.sessionLogin, this.deviceId);
    this.message = this.$filter.transform('remoteControl_Wait_User_Accept_Login');
    this.clearError();
  };

  /** Called when the RC session has ended */
  sessionEnded = () => {
    this.updateSessionEnd();
    this.deviceStateRegistry.removeRemoteControlEndedCallback(this.sessionEnded, this.deviceId);
    this.deviceStateRegistry.removeRemoteControlErrorCallback(this.sessionError, this.deviceId);

    if (this.popoutRemoteControlStopped) {
      this.popoutRemoteControlStopped.close();
    }

    if (this.deviceState.hasError) {
      this.setError(
        this.deviceState.errorMessage
          ? this.deviceState.errorMessage
          : this.$filter.transform('remoteControl_UnknownErrorOccurred')
      );
    }

    this.updateActivityState();
    this.activityCtrl.onRefresh.emit();

    if (!this.error) {
      this.activityCtrl.onDelete.emit();
    }
  };

  /** Called when an RC session error occurs */
  sessionError = () => {
    if (!this) {
      return;
    }
    this.deviceStateRegistry.removeRemoteControlStartedCallback(this.sessionStarted, this.deviceId);

    this.updateSessionEnd();

    if (this.deviceState.hasError && this.deviceState.errorMessage) {
      this.setError(this.deviceState.errorMessage);
    } else {
      this.setError(this.$filter.transform('remoteControl_UnknownErrorOccurred'));
    }
  };

  /** Attempts to restart the RC session */
  restartRemoteControl() {
    // Don't allow restarting a previously started RC session
    if (!this.activity.creationDate) {
      this.clearError();

      this.deviceStateRegistry.setRemoteControlStartedCallback(this.sessionStarted, this.deviceId);
      this.deviceStateRegistry.setRemoteControlErrorCallback(this.sessionError, this.activity.details.deviceId);
      this.remoteControlService.startRemoteControl(this.deviceState.device, this.ticketContextService.ticket.ticketId);
    }
  }

  minimizeToggle() {
    this.ticketPageService.workPanelCollapse(true);
    this.ticketPageService.sidePanelCollapse(false);
    if (this.deviceState?.device) {
      this.deviceStateRegistry.minimizeToggle(this.deviceState.device);
    }
  }

  onClosingRC(device: IDeviceModel) {
    this.ticketPageService.workPanelCollapse(true);
    this.ticketPageService.sidePanelCollapse(false);
    if (this.stopRemoteControl(device)) {
      this.globalEventBusService.broadcast(GlobalEvents.remoteControlCloseInitiated, null);
      this.globalEventBusService.broadcast(GlobalEvents.closeRcWorkPanel, null);
      this.activityCtrl.delete();
    }
  }

  /** Stops the current RC session
   * returns true if the session was closed, false if it was not
   */
  stopRemoteControl(device: IDeviceModel): boolean {
    // If we need to confirm closing the RC session..
    if (this.deviceStateRegistry.isDiscardBlocked()) {
      if (!this.$window.confirm(this.deviceStateRegistry.getDiscardBlockedReason())) {
        return false;
      }
    }
    if (this.remoteControlService.stopRemoteControl(device)) {
      // returns false means user means user clicks cancel in the confirmation dialog
      this.durationUpdate();
      return true;
    }
    return false;
  }

  /** Cancels an RC session that is in an error state */
  cancel() {
    this.stopRemoteControl(this.deviceState.device);
    this.clearError();
    if (!this.activity.creationDate) {
      // RC activity hasn't started yet, so remove the activity
      this.activityCtrl.onDelete.emit();
    }
  }

  /** Determines if the RC session is currently active */
  isOpenSession(): boolean {
    return this.deviceStateRegistry.isRemoteControlConnected(this.deviceId) || this.isNewSession;
  }

  getPopoutIsDisabled(): boolean {
    return !this.deviceStateRegistry.canPopout(this.deviceState.device) || this.isExternallyDisabled;
  }

  getPopoutToolTip(): string {
    if (this.getPopoutIsDisabled()) {
      return this.$filter.transform('remoteControlPopout_Disabled_While_Busy');
    }
    return '';
  }

  popout() {
    if (!this.getPopoutIsDisabled()) {
      if (this.workPanelManagerService.isDeviceUsedByPanelType(this.deviceState.device, WorkPanelType.rc)) {
        this.globalEventBusService.broadcast(GlobalEvents.popOutStarted, WorkPanelType.rc);
      } else if (
        this.workPanelManagerService.isDeviceUsedByPanelType(this.deviceState.device, WorkPanelType.liveSnapshot)
      ) {
        this.globalEventBusService.broadcast(GlobalEvents.popOutStarted, WorkPanelType.liveSnapshot);
      }
    }
  }

  allowOverride(): boolean {
    return (
      this.error === this.$filter.transform('remoteControl_AlreadyInUse_Message') &&
      this.featureToggleService.isEnabled('RcOverride')
    );
  }

  private setDeviceId(deviceId: string) {
    this.deviceId = deviceId;
    this.setDeviceState();
  }

  private setDeviceState() {
    this.deviceState = this.deviceStateRegistry.getOrCreateState(this.deviceId);
  }

  private setError(error: string) {
    this.error = error;
    // this.clearErrorUnbind = this.$rootScope.$on('clearErrorSessions', this.cancel);
    this.deviceStateRegistry.removeRemoteControlErrorCallback(this.sessionError, this.deviceId);
    this.updateActivityState();
  }

  private clearError() {
    this.error = undefined;
    this.deviceState.hasError = false;
    this.deviceState.errorMessage = null;

    // unbinding "clearErrorSession" event when clear error
    // if (this.clearErrorUnbind) {
    //   this.clearErrorUnbind();
    // }

    this.updateActivityState();
  }

  private updateSessionEnd() {
    if (this.activity.creationDate && !this.activity.details.terminationDate) {
      this.isNewSession = false;
      this.activity.details.terminationDate = new Date().toISOString();
    }
    this.durationUpdate();
    if (this.activityCtrl.onUpdate) {
      this.activityCtrl.onUpdate.emit();
    }
    this.message = null;
  }

  private updateActivityState() {
    if (this.error) {
      this.activityCtrl.setActivityState('error');
    } else {
      this.activityCtrl.setActivityState(this.isNewSession || this.isOpenSession() ? 'active' : 'inactive');
    }
  }
}
